/**
 * PDF — viewing metadata and annotation editing (§8 Phase 1, §14).
 *
 * Scope, stated plainly so the catalog and the code agree (§19):
 *
 * - **Annotations are real PDF annotation objects.** Highlights, notes and ink
 *   are written as `/Annot` dictionaries in the page's `/Annots` array, so any
 *   other reader sees them as annotations — not as drawings burned into the
 *   page. The original content stream is never touched, which is what makes
 *   annotation editing non-destructive.
 * - **Rendering and text search are not here.** Those need pdf.js and a canvas,
 *   which belong to the UI layer. This handler reads structure and writes
 *   annotations; it does not claim to rasterise pages.
 * - **Redaction is not implemented.** §14 forbids the black-rectangle
 *   impersonation of redaction, and real redaction — removing the underlying
 *   glyphs and images from the content stream so they cannot be recovered — is
 *   not built yet. Offering the fake would be worse than offering nothing.
 * - **Nothing in the file is executed.** Embedded JavaScript, `/Launch`,
 *   `/URI` and `/GoToR` actions are never followed (§30).
 */
import { PDFDocument, PDFName, PDFRawStream } from 'pdf-lib';
import type { PDFPage } from 'pdf-lib';
import { FORMATS_BY_ID } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';

export interface PdfPageInfo {
  readonly index: number;
  readonly width: number;
  readonly height: number;
  readonly rotation: number;
  readonly existingAnnotations: number;
}

export type RgbColor = readonly [r: number, g: number, b: number];

export interface HighlightAnnotation {
  readonly kind: 'highlight';
  readonly page: number;
  /** PDF user-space coordinates: origin bottom-left. */
  readonly rect: readonly [x1: number, y1: number, x2: number, y2: number];
  readonly color: RgbColor;
  readonly note?: string;
  readonly author?: string;
}

export interface NoteAnnotation {
  readonly kind: 'note';
  readonly page: number;
  readonly x: number;
  readonly y: number;
  readonly contents: string;
  readonly author?: string;
}

export interface InkAnnotation {
  readonly kind: 'ink';
  readonly page: number;
  /** One or more strokes, each a flat [x0,y0, x1,y1, …] list. */
  readonly strokes: readonly (readonly number[])[];
  readonly color: RgbColor;
  readonly width: number;
  readonly author?: string;
}

export type PdfAnnotation = HighlightAnnotation | NoteAnnotation | InkAnnotation;

export interface PdfMetadata {
  readonly title: string | null;
  readonly author: string | null;
  readonly subject: string | null;
  readonly creator: string | null;
  readonly producer: string | null;
}

export interface PdfModel {
  readonly pageCount: number;
  readonly pages: readonly PdfPageInfo[];
  readonly metadata: PdfMetadata;
  /** Annotations queued by the user; written at export time (§13). */
  readonly annotations: readonly PdfAnnotation[];
  /** The file declares an embedded JavaScript action somewhere. */
  readonly hasEmbeddedScripts: boolean;
  readonly hasAttachments: boolean;
  /** No extractable text layer — a scan. Search will not work (§14). */
  readonly looksScanned: boolean;
}

const DEFAULT_AUTHOR = 'Modu Viewer';

/* ------------------------------------------------------------------ *
 * loading
 * ------------------------------------------------------------------ */

async function load(bytes: Uint8Array): Promise<PDFDocument> {
  let doc: PDFDocument;
  try {
    doc = await PDFDocument.load(bytes, {
      // Never silently open a protected document: §14 and §34 require the user
      // to be told it is password protected rather than shown a broken render.
      ignoreEncryption: false,
      updateMetadata: false,
      // Real-world PDFs are full of small structural sins and refusing them all
      // would make the viewer useless. Leniency here is deliberate — but it is
      // bounded by the page check below.
      throwOnInvalidObject: false,
    });
  } catch (err) {
    const message = (err as Error).message ?? '';
    if (/encrypt/i.test(message)) {
      throw new HandlerError('error.passwordProtected', message);
    }
    throw new HandlerError('error.damagedOrIncomplete', `could not parse PDF: ${message}`);
  }

  // A truncated download parses "successfully" and yields a document with no
  // pages. Presenting that as an empty PDF would tell the user their file is
  // blank when in fact it is incomplete — §34 has a message for exactly this,
  // and it is not "here is your empty document".
  if (doc.getPageCount() === 0) {
    throw new HandlerError(
      'error.damagedOrIncomplete',
      'the document parsed but contains no pages, which indicates truncated or corrupt input',
    );
  }

  return doc;
}

function readMetadata(doc: PDFDocument): PdfMetadata {
  const safe = <T>(fn: () => T): T | null => {
    try { return fn(); } catch { return null; }
  };
  return {
    title: safe(() => doc.getTitle() ?? null),
    author: safe(() => doc.getAuthor() ?? null),
    subject: safe(() => doc.getSubject() ?? null),
    creator: safe(() => doc.getCreator() ?? null),
    producer: safe(() => doc.getProducer() ?? null),
  };
}

function countAnnots(page: PDFPage): number {
  try {
    const annots = page.node.Annots();
    return annots ? annots.size() : 0;
  } catch {
    return 0;
  }
}

function pageInfo(doc: PDFDocument): PdfPageInfo[] {
  return doc.getPages().map((page, index) => {
    const { width, height } = page.getSize();
    return {
      index,
      width,
      height,
      rotation: page.getRotation().angle,
      existingAnnotations: countAnnots(page),
    };
  });
}

/**
 * Look for the markers that matter for what we can honestly offer.
 *
 * A raw scan of the source bytes rather than an object walk: the point is to
 * *warn*, and a false positive costs a needless notice while a false negative
 * costs the user a wrong promise about search working.
 */
function scanForFeatures(bytes: Uint8Array): {
  hasEmbeddedScripts: boolean; hasAttachments: boolean;
} {
  // Only the first few megabytes; markers appear in the catalog and page
  // dictionaries, not buried in image data.
  const head = bytes.subarray(0, Math.min(bytes.length, 4 * 1024 * 1024));
  const text = new TextDecoder('latin1').decode(head);
  return {
    hasEmbeddedScripts: text.includes('/JavaScript') || text.includes('/JS')
      || text.includes('/Launch') || text.includes('/OpenAction'),
    hasAttachments: text.includes('/EmbeddedFile'),
  };
}

/**
 * A page with no font resources has no text to extract.
 *
 * Scanned PDFs are page images, and §14 requires telling the user that search
 * will not find anything rather than letting them conclude the search is broken.
 */
function looksScanned(doc: PDFDocument): boolean {
  const pages = doc.getPages();
  if (pages.length === 0) return false;
  const sample = pages.slice(0, Math.min(pages.length, 5));
  let withFonts = 0;
  let withImages = 0;

  for (const page of sample) {
    try {
      const resources = page.node.Resources();
      if (!resources) continue;
      const fonts = resources.lookup(PDFName.of('Font'));
      const xobjects = resources.lookup(PDFName.of('XObject'));
      if (fonts) withFonts++;
      if (xobjects) withImages++;
    } catch { /* unreadable resources — no conclusion */ }
  }
  return withFonts === 0 && withImages > 0;
}

/* ------------------------------------------------------------------ *
 * annotation writing
 * ------------------------------------------------------------------ */

function attachAnnotation(doc: PDFDocument, page: PDFPage, dict: Record<string, unknown>): void {
  const obj = doc.context.obj(dict as never);
  const ref = doc.context.register(obj);
  const annots = page.node.Annots();
  if (annots) annots.push(ref);
  else page.node.set(PDFName.of('Annots'), doc.context.obj([ref]));
}

/** Flag bit 3 (value 4) = Print: the annotation appears when printed. */
const ANNOT_FLAG_PRINT = 4;

function writeAnnotation(doc: PDFDocument, annotation: PdfAnnotation): void {
  const pages = doc.getPages();
  const page = pages[annotation.page];
  if (!page) {
    throw new HandlerError('error.unableToRead', `page ${annotation.page} does not exist`);
  }
  const author = annotation.author ?? DEFAULT_AUTHOR;

  if (annotation.kind === 'highlight') {
    const [x1, y1, x2, y2] = annotation.rect;
    attachAnnotation(doc, page, {
      Type: 'Annot',
      Subtype: 'Highlight',
      Rect: [x1, y1, x2, y2],
      // Quad order is top-left, top-right, bottom-left, bottom-right.
      QuadPoints: [x1, y2, x2, y2, x1, y1, x2, y1],
      C: [...annotation.color],
      CA: 0.4,
      T: author,
      Contents: annotation.note ?? '',
      F: ANNOT_FLAG_PRINT,
    });
    return;
  }

  if (annotation.kind === 'note') {
    const size = 20;
    attachAnnotation(doc, page, {
      Type: 'Annot',
      Subtype: 'Text',
      Rect: [annotation.x, annotation.y, annotation.x + size, annotation.y + size],
      Contents: annotation.contents,
      T: author,
      Name: 'Comment',
      F: ANNOT_FLAG_PRINT,
    });
    return;
  }

  const xs: number[] = [];
  const ys: number[] = [];
  for (const stroke of annotation.strokes) {
    for (let i = 0; i < stroke.length; i += 2) {
      xs.push(stroke[i]!);
      ys.push(stroke[i + 1]!);
    }
  }
  if (xs.length === 0) throw new HandlerError('error.unableToRead', 'ink annotation has no points');
  const pad = annotation.width;

  attachAnnotation(doc, page, {
    Type: 'Annot',
    Subtype: 'Ink',
    Rect: [Math.min(...xs) - pad, Math.min(...ys) - pad, Math.max(...xs) + pad, Math.max(...ys) + pad],
    InkList: annotation.strokes.map((s) => [...s]),
    C: [...annotation.color],
    BS: { W: annotation.width },
    T: author,
    Contents: '',
    F: ANNOT_FLAG_PRINT,
  });
}

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

export const pdfHandler: FormatHandler<PdfModel> = {
  id: 'pdf',
  formatIds: ['pdf'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const doc = await load(source.bytes);
    const pages = doc.getPages();
    const first = pages[0];

    return {
      kind: 'pages',
      unitCount: pages.length,
      unitKey: 'viewer.page',
      firstContent: first
        ? { width: first.getSize().width, height: first.getSize().height, rotation: first.getRotation().angle }
        : null,
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<PdfModel>> {
    throwIfAborted(ctx.signal);
    const doc = await load(source.bytes);
    const features = scanForFeatures(source.bytes);
    const scanned = looksScanned(doc);
    const spec = FORMATS_BY_ID.get('pdf');

    const warnings: HandlerWarning[] = [];
    if (features.hasEmbeddedScripts) {
      warnings.push({
        messageKey: 'detect.warning.macrosBlocked',
        severity: 'caution',
        detail: 'This PDF declares script or launch actions. They are preserved in the file but never run (§30).',
      });
    }
    if (features.hasAttachments) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'caution',
        detail: 'This PDF contains embedded files. They are never opened or executed (§30).',
      });
    }
    if (scanned) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: 'This looks like a scanned document: it has no text layer, so text search will find nothing (§14).',
      });
    }

    return {
      formatId: 'pdf',
      model: {
        pageCount: doc.getPageCount(),
        pages: pageInfo(doc),
        metadata: readMetadata(doc),
        annotations: [],
        hasEmbeddedScripts: features.hasEmbeddedScripts,
        hasAttachments: features.hasAttachments,
        looksScanned: scanned,
      },
      readOnly: ctx.assessment.forceReadOnly,
      editLevel: spec?.edit ?? 'annotate-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  async exportBytes(
    model: PdfModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    const doc = await load(source.bytes);

    for (const annotation of model.annotations) {
      writeAnnotation(doc, annotation);
    }

    const bytes = await doc.save({
      // Object streams compress well but some older readers and many
      // validators handle the plain form more reliably. For a viewer whose
      // output must open everywhere, compatibility wins over a few kilobytes.
      useObjectStreams: false,
      addDefaultPage: false,
    });

    const dropped: string[] = [];
    // pdf-lib rewrites the file rather than appending an incremental update,
    // so a document that relied on incremental structure is normalised. The
    // page content itself is untouched.
    if (model.hasAttachments) {
      dropped.push('일부 생성기의 증분 갱신 구조가 정규화될 수 있습니다');
    }

    return {
      bytes: new Uint8Array(bytes),
      suggestedName: source.filename,
      formatId: 'pdf',
      dropped,
    };
  },

  async validateExport(
    bytes: Uint8Array, model: PdfModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const doc = await load(bytes);
      reopened = true;

      if (doc.getPageCount() !== model.pageCount) {
        issues.push(`page count changed: ${model.pageCount} → ${doc.getPageCount()}`);
      }

      // Every queued annotation must be present on its page.
      const expectedPerPage = new Map<number, number>();
      for (const a of model.annotations) {
        expectedPerPage.set(a.page, (expectedPerPage.get(a.page) ?? 0) + 1);
      }
      const pages = doc.getPages();
      for (const [pageIndex, added] of expectedPerPage) {
        const before = model.pages[pageIndex]?.existingAnnotations ?? 0;
        const page = pages[pageIndex];
        if (!page) { issues.push(`page ${pageIndex} missing after save`); continue; }
        const after = countAnnots(page);
        if (after < before + added) {
          issues.push(`page ${pageIndex}: expected ${before + added} annotations, found ${after}`);
        }
      }

      contentPreserved = issues.length === 0;
    } catch (err) {
      issues.push(`could not reopen the exported PDF: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
  },
};

/* ------------------------------------------------------------------ *
 * editing helpers
 * ------------------------------------------------------------------ */

/** Queue an annotation. Nothing is written until export (§13). */
export function addAnnotation(model: PdfModel, annotation: PdfAnnotation): PdfModel {
  if (annotation.page < 0 || annotation.page >= model.pageCount) {
    throw new HandlerError('error.unableToRead', `page ${annotation.page} is out of range`);
  }
  return { ...model, annotations: [...model.annotations, annotation] };
}

export function removeAnnotation(model: PdfModel, index: number): PdfModel {
  return { ...model, annotations: model.annotations.filter((_, i) => i !== index) };
}

export function clearAnnotations(model: PdfModel): PdfModel {
  return { ...model, annotations: [] };
}

/** Yellow, the colour every PDF reader shows for a default highlight. */
export const HIGHLIGHT_YELLOW: RgbColor = [1, 0.92, 0.23];
export const INK_RED: RgbColor = [0.85, 0.15, 0.15];

export { PDFRawStream };
